import React, { useEffect, useState } from 'react';
import { FlightDetails, FlightStatus } from '../interfaces/flight.details';
import { RiArrowDropRightLine } from 'react-icons/ri';
import { BlinkBlur } from 'react-loading-indicators';
import toast from 'react-hot-toast';
import { useFids } from '../hooks/useFids';
import { RemarkToCodeMapping, TripType } from '@industronics/fids-utils';
import { useAirport } from '../context/AirportContext';
import FlightDetailPopup from './FlightDetailPopup';
import moment from 'moment';
import { formatDate } from '../utils/common.utils';
import TimeBlock from './TimeBlock';
import SafeFlightLogo from './SafeFlightLogo';


interface DepartureProps {
    searchText: string;
}

const gmtOffsetInt = Math.round(-new Date().getTimezoneOffset() / 60); // 8 for GMT+8

const Departures: React.FC<DepartureProps> = ({ searchText }) => {

    const { airportId } = useAirport();
    const { trips, loading, error } = useFids(airportId ?? '', TripType.Departure);
    const [filteredData, setFilteredData] = useState<FlightDetails[]>([]);
    const [stationIndices, setStationIndices] = useState<{ [key: string]: number }>({});
    const [selectedFlight, setSelectedFlight] = useState<FlightDetails | null>(null);

    const checkLastStation = (flight: FlightDetails) => {
        if (flight.stations.length - 1 ===
            (stationIndices[flight._id] % (flight.stations.length - 1)) + 1
        ) return true;
        return false;
    };

    const status = (remark: FlightStatus): React.ReactNode => {
        if (!remark) return null;
        const text = remark.description ?? '';

        switch (remark.key) {
            case RemarkToCodeMapping.FINAL_CALL:
                return <p className="text-orange-500 font-bold">{text}</p>;
            case RemarkToCodeMapping.BOARDING:
            case RemarkToCodeMapping.NEW_GATE:
            case RemarkToCodeMapping.DEPARTED:
                return <p className="text-green-500 font-bold">{text}</p>;
            case RemarkToCodeMapping.GATE_CLOSED:
            case RemarkToCodeMapping.GATE_OPEN:
                return <p className="text-blue-500 font-bold">{text}</p>;
            case RemarkToCodeMapping.CANCELLED:
            case RemarkToCodeMapping.DELAYED:
                return <p className="text-red-500 font-bold">{text}</p>;
            default:
                // fallback: render plain text, not the object
                return <p className="font-bold">{text}</p>;
        }
    };

    const statusMobileView = (remark: FlightStatus): React.ReactNode => {
        if (!remark) return null;
        const text = remark.description ?? '';

        switch (remark.key) {
            case RemarkToCodeMapping.DELAYED:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400">{text}</span>;
            case RemarkToCodeMapping.BOARDING:
            case RemarkToCodeMapping.DEPARTED:
                return <span className="bg-green-100 text-green-800 text-xs me-2 px-2.5 py-0.5 rounded border border-green-400">{text}</span>;
            case RemarkToCodeMapping.NEW_GATE:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400">{text}</span>;
            case RemarkToCodeMapping.GATE_CLOSED:
                return <span className="bg-gray-100 text-gray-800 text-xs me-2 px-2.5 py-0.5 rounded border border-gray-400">{text}</span>;
            case RemarkToCodeMapping.GATE_OPEN:
                return <span className="bg-blue-100 text-blue-800 text-xs me-2 px-2.5 py-0.5 rounded border border-blue-400">{text}</span>;
            case RemarkToCodeMapping.CANCELLED:
                return <span className="bg-red-100 text-red-800 text-xs me-2 px-2.5 py-0.5 rounded border border-red-400">{text}</span>;
            case RemarkToCodeMapping.FINAL_CALL:
                return <span className="bg-orange-100 text-orange-800 text-xs me-2 px-2.5 py-0.5 rounded border border-orange-400">{text}</span>;
            default:
                return <span className="text-xs">{text}</span>;
        }
    };

    const setStations = async () => {
        try {
            const initialIndices: { [key: string]: number } = {};
            trips.forEach((flight: FlightDetails) => {
                initialIndices[flight._id] = 0;
            });
            setStationIndices(initialIndices);
        } catch (error) {
            if (error instanceof Error) {
                toast.error(error.message || "Failed to fetch flight data.");
            } else {
                toast.error("Failed to fetch flight data.");
            }
        } finally {
        }
    };

    useEffect(() => {
        setStations()
    }, [trips]);


    // Update stationIndices for flight stations
    useEffect(() => {
        const stationIntervalId = setInterval(() => {
            setStationIndices((prevIndices) => {
                const newIndices = { ...prevIndices };
                trips.forEach((flight) => {
                    if (flight.stations && flight.stations.length > 1) {
                        const currentIndex = prevIndices[flight._id] || 0;
                        newIndices[flight._id] = (currentIndex + 1) % (flight.stations.length - 1); // Exclude the final destination
                    }
                });
                return newIndices;
            });
        }, 3000);

        return () => {
            clearInterval(stationIntervalId);
        };
    }, [trips]);

    useEffect(() => {
        const normalize = (str: string) => String(str || '').toLowerCase().replace(/\s+/g, '');
        if (searchText) {
            const normalizedSearch = normalize(searchText);

            const filtered = trips.filter((flight) => {
                const matchesFlightCode = normalize(String(flight.flightNumber || '')).includes(normalizedSearch);
                const matchesCodeShare = flight.codeShareFlight?.some((codeShare: string) =>
                    normalize(String(codeShare || '')).includes(normalizedSearch)
                );
                const matchesAirline = normalize(String(flight?.airline?.name || '')).includes(normalizedSearch);
                const matchesDestination =
                    flight.stations.slice(1).filter(x => normalize(String(x.city || '')).includes(normalizedSearch)).length > 0;

                return matchesFlightCode || matchesCodeShare || matchesAirline || matchesDestination;
            });

            setFilteredData(filtered);
        } else {
            setFilteredData(trips);
        }
    }, [searchText, trips]);


    if (!airportId) {
        return <div className="w-full p-6 text-center">Please select an airport to view departures.</div>;
    }

    return (
        <div className="w-full">
            <div>
                <div className="hidden md:block w-full overflow-x-auto">
                    {!loading ? (
                        <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                            <thead className="text-xs text-gray-700 sticky uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                <tr>
                                    <th scope="col" className="px-6 py-3 rounded-tl-lg rounded-bl-lg">Flight No.</th>
                                    <th scope="col" className="px-6 py-3">CodeShare</th>
                                    <th scope="col" className="px-6 py-3">Destination</th>
                                    <th scope="col" className="px-6 py-3">Scheduled Time</th>
                                    <th scope="col" className="px-6 py-3">Estimated Time</th>
                                    <th scope="col" className="px-6 py-3">Actual Time</th>
                                    <th scope="col" className="px-6 py-3 text-center">Terminal</th>
                                    <th scope="col" className="px-6 py-3 text-center">Check-In Counter</th>
                                    <th scope="col" className="px-6 py-3 rounded-tr-lg rounded-br-lg">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData?.length > 0 ? (
                                    filteredData.map((flight: FlightDetails, index) => (
                                        <tr key={index} onClick={() => setSelectedFlight(flight)} className="bg-inherit border-b text-gray-900 font-semibold transition-transform duration-200 ease-in-out hover:scale-105 hover:bg-slate-200 cursor-pointer">
                                            <th className="px-6 py-4 font-bold text-gray-900 whitespace-nowrap dark:text-white">
                                                <div className="flex items-center gap-2">
                                                    <SafeFlightLogo dimension='0180x0045' mainFlightNumber={flight.flightNumber} logoSize={{width:80, height: 40}}/>
                                                    <span>{flight.flightNumber}</span>
                                                </div>
                                            </th>
                                            <td className="px-6 py-4">
                                                {Array.isArray(flight.codeShareFlight) ? (
                                                    <ul>
                                                        {flight.codeShareFlight.map((item: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, index: React.Key | null | undefined) => (
                                                            <li key={index}>{item}</li>
                                                        ))}
                                                    </ul>
                                                ) : ('')}
                                            </td>
                                            <td className="px-6 py-4">
                                                {flight?.stations.length > 0 && (
                                                    <div
                                                        style={{ textDecoration: flight.stations.length > 2 && checkLastStation(flight) ? "underline" : "initial" }}
                                                        className="px-6 py-4 text-blue-800 text-lg station-animation"
                                                        key={"station" + stationIndices[flight._id]}
                                                    >
                                                        {(flight?.stations[
                                                            (stationIndices[flight._id] % (flight?.stations?.length - 1)) + 1
                                                        ]?.city)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <TimeBlock date={flight.scheduledTravelTime} />
                                            </td>
                                            <td className="px-6 py-4">{flight.ETA && flight.ETA !== flight.scheduledTravelTime && <TimeBlock date={flight.ETA} />}</td>
                                            <td className="px-6 py-4">
                                                {flight.actualArrivalDepartureTime && flight.actualArrivalDepartureTime !== flight.scheduledTravelTime && <TimeBlock date={flight.actualArrivalDepartureTime} />}
                                            </td>
                                            <td className="px-6 py-4 text-center">{flight?.terminal?.code}</td>
                                            <td className="px-6 py-4 text-center">{flight?.checkInDeskRange}</td>
                                            <td className="px-6 py-4">{status(flight.remarkCode)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={10} className="h-96 text-center text-lg font-semibold text-gray-500">
                                            No Record Found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    ) : (
                        <div className="flex justify-center items-center h-96">
                            <BlinkBlur color="#437cc6" size="small" text="Getting Flights Ready..." textColor="" />
                        </div>
                    )
                    }
                </div>


                <div className="block md:hidden p-2">
                    {!loading ? (
                        <div className={`${filteredData.length > 0 ? 'shadow-xl' : ''}`}>
                            {filteredData.length > 0 ? (
                                <div>
                                    {filteredData.map((flight: FlightDetails, index) => (
                                        <div
                                            key={index}
                                            className="bg-white border-b border-gray-300 p-4 flex gap-x-4"
                                            onClick={() => setSelectedFlight(flight)}
                                        >
                                            <div className="flex-none pt-1">
                                                <div className='w-14'><SafeFlightLogo dimension='0180x0045' mainFlightNumber={flight.flightNumber} logoSize={{width:80, height: 40}}/></div>
                                                <p className={`text-md ${flight.ETA && flight.ETA !== flight.scheduledTravelTime ? 'line-through text-blue-800' : 'font-semibold text-blue-600'}`}>
                                                    {<span>
                                                        {moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).format("HH:mm")}
                                                        <span className='text-blue-800' style={{ display: "block" }}>
                                                            {
                                                                moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).isSame(moment(), 'day')
                                                                    ? ''
                                                                    : (
                                                                        <span
                                                                        >{moment(flight.scheduledTravelTime).utcOffset(gmtOffsetInt).format("DD MMM")}</span>
                                                                    )}
                                                        </span>
                                                    </span>}
                                                </p>
                                                <p className='font-semibold text-md text-blue-600'>
                                                    {flight.ETA && flight.ETA !== flight.scheduledTravelTime && <span>
                                                        {moment(flight.ETA).utcOffset(gmtOffsetInt).format("HH:mm")}
                                                        <span className='text-blue-800' style={{ display: "block" }}>
                                                            {
                                                                moment(flight.ETA).utcOffset(gmtOffsetInt).isSame(moment(), 'day')
                                                                    ? ''
                                                                    : (
                                                                        <span
                                                                        >{moment(flight.ETA).utcOffset(gmtOffsetInt).format("DD MMM")}</span>
                                                                    )}
                                                        </span>
                                                    </span>}
                                                </p>
                                            </div>
                                            <div className="flex grow justify-between">
                                                <div className="text-left">
                                                    <div className='font-bold text-lg text-blue-900'>
                                                        {flight.stations && flight.stations.length > 1 && (
                                                            <div
                                                                style={{ textDecoration: flight.stations.length > 2 && checkLastStation(flight) ? "underline" : "initial" }}
                                                                className="px-6 py-4 text-blue-800 text-lg station-animation"
                                                                key={"station" + stationIndices[flight._id]}
                                                            >
                                                                {(flight?.stations[
                                                                    (stationIndices[flight._id] % (flight?.stations?.length - 1)) + 1
                                                                ]?.translations?.find(x => x?.lang === "en")?.value)}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <p className='font-normal text-md text-blue-800'>{flight.flightNumber}</p>

                                                    {Array.isArray(flight.codeShareFlight) && flight.codeShareFlight.length > 0 ? (
                                                        <ul>
                                                            {flight.codeShareFlight.map((item: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined, i: React.Key | null | undefined) => (
                                                                <li className='font-normal text-md text-blue-800' key={i}>{item}</li>
                                                            ))}
                                                        </ul>
                                                    ) : ''}

                                                </div>
                                                <div className="text-right flex font-semibold">
                                                    <div className='text-left'>
                                                        {statusMobileView(flight.remarkCode)}
                                                        {
                                                            flight?.checkInStatus && flight?.checkInStatus?.key !== RemarkToCodeMapping.DESK_CLOSED ? (
                                                                <div>
                                                                    <div className='text-xs font-light me-2'>
                                                                        <span className='font-medium text-gray-800'>Check-In</span>
                                                                        <p>
                                                                            <span className='text-blue-600 font-medium'>
                                                                                {(flight?.checkInStatus?.description || "")}
                                                                            </span>
                                                                            <br />
                                                                            <span>{formatDate(flight?.checkInRowOpeningTime, false) + '-' + formatDate(flight?.checkInRowClosingTime, false)}</span>
                                                                        </p>
                                                                    </div>
                                                                </div>) : ''
                                                        }

                                                    </div>
                                                    <RiArrowDropRightLine className='font-bold text-blue-800' size={30} />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (<div className="flex justify-center items-center h-96 bg-transparent font-semibold">No Record Found</div>)}
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-96">
                            <BlinkBlur color="#437cc6" size="small" text="Getting Flights Ready..." textColor="" />
                        </div>
                    )
                    }
                </div>
            </div>

            {
                selectedFlight && (
                    <FlightDetailPopup
                        flight={selectedFlight}
                        onClose={() => setSelectedFlight(null)}
                        modalTitle="Departure"
                    />
                )
            }
        </div >
    );
};

export default Departures;
